import { Injectable, signal, computed, inject, effect } from '@angular/core';
import { SupabaseService } from './supabase.service';
import { Partner } from '../models/models';

export interface Address {
    id: string;
    label: string;
    street: string;
    city: string;
    zipCode: string;
    isDefault?: boolean;
    latitude?: number;
    longitude?: number;
}

export interface FavoriteItem {
    partnerId: string;
    menuItemId: string;
    addedAt: Date;
}

export interface UserProfile {
    name: string;
    email: string;
    avatar?: string;
}

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private supabaseService = inject(SupabaseService);

    userProfile = signal<UserProfile>({
        name: '',
        email: '',
        avatar: ''
    });

    async refreshProfile() {
        const user = this.supabaseService.currentUser();
        if (user) {
            const profile = await this.supabaseService.getProfile();
            this.userProfile.set({
                name: profile?.name || user.email?.split('@')[0] || 'Usuario',
                email: user.email || '',
                avatar: profile?.image_url || 'assets/icon.png'
            });
        }
    }

    myPartner = computed(() => this.supabaseService.currentUser()?.myPartner ?? null);

    // Role & partner ownership derived from SupabaseService currentUser
    userRole = computed(() => this.supabaseService.currentUser()?.role ?? 'user');
    isPartnerOwner = computed(() => this.userRole() === 'partner_owner');
    isPartnerEmployee = computed(() => this.userRole() === 'partner_employee');
    isAdmin = computed(() => this.userRole() === 'admin');
    hasAdminAccess = computed(() => this.isAdmin() || this.isPartnerOwner() || this.isPartnerEmployee());

    isOwnerOf(partner: Partner | null | undefined): boolean {
        const user = this.supabaseService.currentUser();
        if (!user || user.role !== 'partner_owner' || !partner) return false;
        return (
            (!!user.myPartner?.id && user.myPartner.id === partner.id) ||
            (!!partner.ownerProfileId && partner.ownerProfileId === user.id) ||
            (!!user.myPartner?.nameid && !!partner.nameid && user.myPartner.nameid === partner.nameid)
        );
    }

    addresses = signal<Address[]>([]);
    employeePartners = signal<Partner[]>([]);

    constructor() {
        effect(async () => {
            const user = this.supabaseService.currentUser();
            if (user) {
                // Profile & Addresses (Background)
                this.refreshProfile();
                this.supabaseService.getUserAddresses().then(addr => this.addresses.set(addr));

                if (user.role === 'partner_employee') {
                    this.supabaseService.getEmployeePartners().then(partners => this.employeePartners.set(partners));
                } else {
                    this.employeePartners.set([]);
                }
            } else {
                this.userProfile.set({ name: '', email: '', avatar: '' });
                this.addresses.set([]);
                this.employeePartners.set([]);
            }
        });
    }

    async addAddress(address: Omit<Address, 'id'>) {
        if (!address.latitude || !address.longitude) {
            const coords = await this.supabaseService.geocodeAddress(address.street, address.city, address.zipCode);
            if (coords) {
                address.latitude = coords.lat;
                address.longitude = coords.lng;
            }
        }
        await this.supabaseService.addAddress(address);
        this.addresses.set(await this.supabaseService.getUserAddresses());
    }

    async updateAddress(address: Address) {
        if (!address.latitude || !address.longitude) {
            const coords = await this.supabaseService.geocodeAddress(address.street, address.city, address.zipCode);
            if (coords) {
                address.latitude = coords.lat;
                address.longitude = coords.lng;
            }
        }
        await this.supabaseService.updateAddress(address);
        this.addresses.set(await this.supabaseService.getUserAddresses());
    }

    async deleteAddress(addressId: string) {
        await this.supabaseService.deleteAddress(addressId);
        this.addresses.set(await this.supabaseService.getUserAddresses());
    }

    favorites = signal<FavoriteItem[]>([
        { partnerId: 'bakery1', menuItemId: 'd1', addedAt: new Date() },
        { partnerId: 'r2', menuItemId: 'm4', addedAt: new Date() },
        { partnerId: 'bakery1', menuItemId: 'p1', addedAt: new Date() }
    ]);

    toggleFavorite(partnerId: string, menuItemId: string) {
        this.favorites.update(favs => {
            const exists = favs.some(f => f.partnerId === partnerId && f.menuItemId === menuItemId);
            if (exists) {
                return favs.filter(f => !(f.partnerId === partnerId && f.menuItemId === menuItemId));
            } else {
                return [...favs, { partnerId, menuItemId, addedAt: new Date() }];
            }
        });
    }

    isFavorite(partnerId: string, menuItemId: string): boolean {
        return this.favorites().some(f => f.partnerId === partnerId && f.menuItemId === menuItemId);
    }
}
